import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  useTheme,
  Button
} from '@mui/material';
import {
  Stars,
  EmojiEvents,
  Lightbulb,
  Flag,
  Favorite,
  AutoAwesome,
  ArrowBack
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { getRecognitions, Recognition } from '@/services/DashboardAnalyticsService';

const recognitionTypes = [
  { value: 'star_performer', label: 'Star Performer', icon: Stars, color: '#FFD700' },
  { value: 'team_player', label: 'Team Player', icon: Favorite, color: '#E91E63' },
  { value: 'innovation', label: 'Innovation', icon: Lightbulb, color: '#FF9800' },
  { value: 'milestone', label: 'Milestone', icon: Flag, color: '#4CAF50' },
  { value: 'customer_champion', label: 'Customer Champion', icon: EmojiEvents, color: '#2196F3' },
  { value: 'custom', label: 'Custom', icon: AutoAwesome, color: '#9C27B0' }
];

const getTypeConfig = (type: string) => {
  return recognitionTypes.find(t => t.value === type) || recognitionTypes[5];
};

const WallOfFamePage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [recognitions, setRecognitions] = useState<Recognition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecognitions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getRecognitions(1, 50);
        if (response.success && response.data) {
          setRecognitions(response.data);
        } else {
          setError(response.error?.message || 'Failed to load recognitions');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load recognitions');
      } finally {
        setLoading(false);
      }
    };

    fetchRecognitions();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{ textTransform: 'none' }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmojiEvents sx={{ fontSize: 32, color: '#FFD700' }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Wall of Fame
            </Typography>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress />
          </Box>
        ) : recognitions.length === 0 ? (
          <Card sx={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
            borderRadius: 2,
            textAlign: 'center',
            py: 6
          }}>
            <EmojiEvents sx={{ fontSize: 64, color: '#FFD700', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No recognitions yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Be the first to be recognized!
            </Typography>
          </Card>
        ) : (
          /* Recognitions Grid */
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3
          }}>
            {recognitions.map((recognition) => {
              const typeConfig = getTypeConfig(recognition.recognition_type);
              const IconComponent = typeConfig.icon;

              return (
                <Card
                  key={recognition.id}
                  sx={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider}`,
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[4]
                    },
                    ...(recognition.is_featured && {
                      border: `2px solid ${typeConfig.color}`,
                      boxShadow: `0 0 20px ${typeConfig.color}30`
                    })
                  }}
                >
                  <CardContent>
                    {/* Featured Badge */}
                    {recognition.is_featured && (
                      <Chip
                        label="Featured"
                        size="small"
                        sx={{
                          mb: 2,
                          backgroundColor: `${typeConfig.color}20`,
                          color: typeConfig.color,
                          fontWeight: 600
                        }}
                      />
                    )}

                    {/* Type Icon */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}>
                      <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: `${typeConfig.color}20`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <IconComponent sx={{ color: typeConfig.color }} />
                      </Box>
                      <Chip
                        label={typeConfig.label}
                        size="small"
                        sx={{
                          backgroundColor: `${typeConfig.color}15`,
                          color: typeConfig.color,
                          fontWeight: 500
                        }}
                      />
                    </Box>

                    {/* Title */}
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {recognition.title}
                    </Typography>

                    {/* Description */}
                    {recognition.description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2, lineHeight: 1.6 }}
                      >
                        {recognition.description}
                      </Typography>
                    )}

                    {/* Employee Info */}
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      pt: 2,
                      borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : theme.palette.divider}`
                    }}>
                      <Avatar
                        sx={{ width: 36, height: 36, bgcolor: typeConfig.color }}
                      >
                        {recognition.employee_name?.charAt(0)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {recognition.employee_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(recognition.created_at)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Recognized By */}
                    {recognition.recognized_by_name && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Recognized by {recognition.recognized_by_name}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Layout>
  );
};

export default WallOfFamePage;
